import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";
import * as React from "react";
import { Button, type ButtonVariant } from "../atoms/button.js";
import { cx } from "../internal/cx.js";
import type { DialogSize } from "./dialog.js";

/**
 * AlertDialog — modal confirmation for irreversible or risky actions
 * (delete, terminate, void, publish-immediately, etc).
 *
 * Visually identical to Dialog so the design system feels coherent, but
 * built on Radix AlertDialog so it gets stricter semantics:
 *   - Overlay click does NOT dismiss
 *   - Escape key does NOT dismiss (configurable via Radix events)
 *   - No close (X) button — the user must pick Cancel or Action
 *   - role="alertdialog" with aria-describedby wired automatically
 *
 * Use this whenever you want to make the user pause and confirm.
 */

/* ─── Primitives (Radix re-exports) ─── */

export const AlertDialog = AlertDialogPrimitive.Root;
export const AlertDialogTrigger = AlertDialogPrimitive.Trigger;
export const AlertDialogPortal = AlertDialogPrimitive.Portal;

export const AlertDialogOverlay = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Overlay>
>(function AlertDialogOverlay({ className, ...props }, ref) {
  return (
    <AlertDialogPrimitive.Overlay
      ref={ref}
      className={cx("cw-v2-dialog__overlay", className)}
      {...props}
    />
  );
});
AlertDialogOverlay.displayName = "AlertDialogOverlay";

export interface AlertDialogContentProps
  extends React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Content> {
  /** Desktop max-width. Defaults to 'sm' since confirmations are usually short. */
  size?: DialogSize;
}

export const AlertDialogContent = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Content>,
  AlertDialogContentProps
>(function AlertDialogContent({ children, className, size = "sm", ...props }, ref) {
  return (
    <AlertDialogPrimitive.Portal>
      <AlertDialogOverlay />
      <AlertDialogPrimitive.Content
        ref={ref}
        className={cx(
          "cw-v2-theme",
          "cw-v2-dialog__content",
          `cw-v2-dialog__content--${size}`,
          className,
        )}
        {...props}
      >
        <div className="cw-v2-dialog__inner">{children}</div>
      </AlertDialogPrimitive.Content>
    </AlertDialogPrimitive.Portal>
  );
});
AlertDialogContent.displayName = "AlertDialogContent";

/* ─── Layout subcomponents — share dialog CSS for visual parity ─── */

export const AlertDialogHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function AlertDialogHeader({ className, ...props }, ref) {
  return <div ref={ref} className={cx("cw-v2-dialog__header", className)} {...props} />;
});
AlertDialogHeader.displayName = "AlertDialogHeader";

export const AlertDialogTitle = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Title>
>(function AlertDialogTitle({ className, ...props }, ref) {
  return (
    <AlertDialogPrimitive.Title
      ref={ref}
      className={cx("cw-v2-dialog__title", className)}
      {...props}
    />
  );
});
AlertDialogTitle.displayName = "AlertDialogTitle";

export const AlertDialogDescription = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Description>
>(function AlertDialogDescription({ className, ...props }, ref) {
  return (
    <AlertDialogPrimitive.Description
      ref={ref}
      className={cx("cw-v2-dialog__description", className)}
      {...props}
    />
  );
});
AlertDialogDescription.displayName = "AlertDialogDescription";

export const AlertDialogBody = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function AlertDialogBody({ className, ...props }, ref) {
  return <div ref={ref} className={cx("cw-v2-dialog__body", className)} {...props} />;
});
AlertDialogBody.displayName = "AlertDialogBody";

export const AlertDialogFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function AlertDialogFooter({ className, ...props }, ref) {
  return <div ref={ref} className={cx("cw-v2-dialog__footer", className)} {...props} />;
});
AlertDialogFooter.displayName = "AlertDialogFooter";

/* ─── Action buttons ─── */

export interface AlertDialogActionProps
  extends Omit<React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Action>, "asChild"> {
  /** Button styling — use 'danger' for destructive confirmations. Default 'primary'. */
  variant?: Extract<ButtonVariant, "primary" | "secondary" | "danger">;
  /** Optional className passthrough applied to the rendered Button. */
  buttonClassName?: string;
}

/**
 * Confirmation action. Renders a Button via Radix's `asChild` so the action
 * inherits the design system's button styling — including the destructive
 * variant for delete-style confirmations.
 */
export const AlertDialogAction = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Action>,
  AlertDialogActionProps
>(function AlertDialogAction({ buttonClassName, children, variant = "primary", ...props }, ref) {
  return (
    <AlertDialogPrimitive.Action asChild {...props}>
      <Button ref={ref} className={buttonClassName} variant={variant}>
        {children}
      </Button>
    </AlertDialogPrimitive.Action>
  );
});
AlertDialogAction.displayName = "AlertDialogAction";

export interface AlertDialogCancelProps
  extends Omit<React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Cancel>, "asChild"> {
  /** Defaults to 'secondary' so it visually steps back from the action. */
  variant?: Extract<ButtonVariant, "secondary" | "ghost" | "outline">;
  buttonClassName?: string;
}

/**
 * The escape hatch — closes the dialog without performing the action.
 * Radix auto-focuses Cancel when the dialog opens, so it doubles as a safe
 * default for accidental Enter presses on destructive flows.
 */
export const AlertDialogCancel = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Cancel>,
  AlertDialogCancelProps
>(function AlertDialogCancel({ buttonClassName, children, variant = "secondary", ...props }, ref) {
  return (
    <AlertDialogPrimitive.Cancel asChild {...props}>
      <Button ref={ref} className={buttonClassName} variant={variant}>
        {children}
      </Button>
    </AlertDialogPrimitive.Cancel>
  );
});
AlertDialogCancel.displayName = "AlertDialogCancel";
